import React, { useState, useEffect } from 'react';
import { FiBarChart2, FiUsers, FiPieChart, FiCalendar, FiDownload, FiMapPin, FiLayers, FiFileText } from 'react-icons/fi';
import SummaryView from './SummaryView';
import QuestionView from './QuestionView';
import IndividualView from './IndividualView';
import MultiSelectFilter from './MultiSelectFilter';
import { studentAPI, reportsAPI } from '../../services/api';
import { downloadBlobResponse } from '../../utils/downloadFile';

const DATE_PRESETS = [
  { id: 'current_year',   label: 'This Year' },
  { id: 'current_month',  label: 'This Month' },
  { id: 'last_30',        label: 'Last 30 Days' },
  { id: 'last_60',        label: 'Last 60 Days' },
  { id: 'last_90',        label: 'Last 90 Days' },
  { id: 'last_6_months',  label: 'Last 6 Months' },
  { id: 'last_12_months', label: 'Last 12 Months' },
  { id: 'all_time',       label: 'All Time' },
];

const getDateRange = (preset) => {
  const now = new Date();
  const endDate = now.toISOString();

  switch (preset) {
    case 'current_year': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { startDate: start.toISOString(), endDate };
    }
    case 'current_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last_30': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last_60': {
      const start = new Date(now);
      start.setDate(start.getDate() - 60);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last_90': {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last_6_months': {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 6);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last_12_months': {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      return { startDate: start.toISOString(), endDate };
    }
    case 'all_time':
    default:
      return { startDate: null, endDate: null };
  }
};

const Reports = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [activePreset, setActivePreset] = useState('current_year');
  const [campuses, setCampuses] = useState([]);
  const [selectedCampuses, setSelectedCampuses] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [exportingReport, setExportingReport] = useState(false);
  const [exportingRawData, setExportingRawData] = useState(false);

  const dateRange = getDateRange(activePreset);

  const tabs = [
    { id: 'summary',    label: 'Summary',    icon: FiPieChart  },
    { id: 'questions',  label: 'Questions',  icon: FiBarChart2 },
    { id: 'individual', label: 'Individual', icon: FiUsers     }
  ];

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [campusesRes, gradeLevelsRes] = await Promise.all([
          studentAPI.getCampuses(),
          studentAPI.getGradeLevels()
        ]);
        setCampuses(campusesRes.data || []);
        setGradeLevels(gradeLevelsRes.data || []);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleExportReport = async () => {
    try {
      setExportingReport(true);
      const response = await reportsAPI.exportPDF({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        campus: selectedCampuses,
        gradeLevel: selectedGrades
      });
      downloadBlobResponse(response, `survey_report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert(`Failed to export report: ${error.message || 'Unknown error'}`);
    } finally {
      setExportingReport(false);
    }
  };

  const handleExportRawData = async () => {
    try {
      setExportingRawData(true);
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      if (selectedCampuses.length) params.campus = selectedCampuses.join(',');
      if (selectedGrades.length) params.gradeLevel = selectedGrades.join(',');

      const response = await studentAPI.exportCSV(params);
      downloadBlobResponse(response, `survey_raw_data_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Error exporting raw data:', error);
      alert(`Failed to export raw data: ${error.message || 'Unknown error'}`);
    } finally {
      setExportingRawData(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Survey Reports</h1>
            <p className="text-gray-600">
              Analyze survey responses and view statistics across all students
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportRawData}
              disabled={exportingRawData}
              className="
                px-5 py-2.5 rounded-lg font-medium text-sm
                bg-white text-gray-700 border border-gray-300 hover:bg-gray-50
                disabled:bg-gray-100 disabled:cursor-not-allowed
                transition-all flex items-center space-x-2 shadow-sm hover:shadow-md
              "
            >
              <FiFileText className="w-4 h-4" />
              <span>{exportingRawData ? 'Exporting...' : 'Export Raw Data'}</span>
            </button>
            <button
              onClick={handleExportReport}
              disabled={exportingReport}
              className="
                px-5 py-2.5 rounded-lg font-medium text-sm
                bg-green-600 text-white hover:bg-green-700
                disabled:bg-green-400 disabled:cursor-not-allowed
                transition-all flex items-center space-x-2 shadow-sm hover:shadow-md
              "
            >
              <FiDownload className="w-4 h-4" />
              <span>{exportingReport ? 'Exporting...' : 'Export Report'}</span>
            </button>
          </div>
        </div>

        {/* Date Range, Campus & Grade Level Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mr-1">
              <FiCalendar className="h-4 w-4" />
              Date Range:
            </div>
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => setActivePreset(preset.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activePreset === preset.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {preset.label}
              </button>
            ))}
            <div className="w-px h-6 bg-gray-200 mx-1" />
            <MultiSelectFilter
              icon={FiMapPin}
              options={campuses.map((c) => ({ value: c, label: c }))}
              selected={selectedCampuses}
              onChange={setSelectedCampuses}
              allLabel="All Campuses"
              filterLabel="Filter by Campus"
              selectedSuffix="Campuses Selected"
            />
            <MultiSelectFilter
              icon={FiLayers}
              options={gradeLevels.map((g) => ({ value: g, label: `Grade ${g}` }))}
              selected={selectedGrades}
              onChange={setSelectedGrades}
              allLabel="All Grades"
              filterLabel="Filter by Grade Level"
              selectedSuffix="Grades Selected"
            />
          </div>
          {(selectedCampuses.length > 0 || selectedGrades.length > 0) && (
            <p className="mt-2 text-xs text-gray-500">
              Reports and exports scoped to:{' '}
              {selectedCampuses.length > 0 && (
                <span className="font-medium text-gray-700">{selectedCampuses.join(', ')}</span>
              )}
              {selectedCampuses.length > 0 && selectedGrades.length > 0 && '   •   '}
              {selectedGrades.length > 0 && (
                <span className="font-medium text-gray-700">
                  {selectedGrades.map((g) => `Grade ${g}`).join(', ')}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === tab.id
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'summary'    && <SummaryView    startDate={dateRange.startDate} endDate={dateRange.endDate} campus={selectedCampuses} gradeLevel={selectedGrades} />}
            {activeTab === 'questions'  && <QuestionView   startDate={dateRange.startDate} endDate={dateRange.endDate} campus={selectedCampuses} gradeLevel={selectedGrades} />}
            {activeTab === 'individual' && <IndividualView startDate={dateRange.startDate} endDate={dateRange.endDate} campus={selectedCampuses} gradeLevel={selectedGrades} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
